import { catalogEntries, type VariantTags } from "@/platform/thecore/ingest";
import { SC2_SOURCES } from "./sc2-sources";
import type { FacetedSetup } from "./setup-model";

/**
 * The Blizzard stock presets modeled as faceted setups: their "edition" is the in-game hotkey preset
 * (Standard / Classic / Grid) and hand is the mouse-hand mirror. This is the small data-model addition
 * that lets the Setup picker facet Blizzard through the same editionAxes / facet machinery as TheCore,
 * rather than as a special case. Classic ships Right-only - no Left mirror is extracted from CASC yet -
 * so its Hand axis offers Right alone until one is; the "list is the declaration" model adapts with no
 * picker change. Insertion order is in-game order (Standard, Classic, Grid) and drives edition display.
 */
const BLIZZARD_VARIANTS: Record<string, VariantTags> = {
  "blizzard-standard": { edition: "standard", hand: "right" },
  "blizzard-standard-left": { edition: "standard", hand: "left" },
  "blizzard-classic": { edition: "classic", hand: "right" },
  "blizzard-grid": { edition: "grid", hand: "right" },
  "blizzard-grid-left": { edition: "grid", hand: "left" },
};

const nameOf = (id: string): string => SC2_SOURCES.find((s) => s.id === id)?.name ?? id;

/**
 * The SC2 setups the picker facets over: the TheCore catalog Presets (which already carry a variant)
 * plus the Blizzard stock presets given variant tags above. Uploads and the Edennil Personal source
 * have no variant and are handled by the picker as flat families, not here. The selectable ids match
 * SC2_SOURCES exactly, so picking a variant resolves to the same source the flat list would select -
 * persistence and deep-links (the source id) are unchanged.
 */
export const FACETED_SC2_SETUPS: readonly FacetedSetup[] = [
  ...catalogEntries.map((e) => ({ id: e.id, name: e.name, source: e.source, variant: e.variant })),
  ...Object.entries(BLIZZARD_VARIANTS).map(([id, variant]) => ({
    id,
    name: nameOf(id),
    source: "Blizzard",
    variant,
  })),
];

/** The ids that facet (TheCore + Blizzard): the picker splits SC2 sources into faceted vs the flat
 * families (uploads, Personal) by testing membership here. */
export const FACETED_SC2_IDS: ReadonlySet<string> = new Set(FACETED_SC2_SETUPS.map((s) => s.id));
